import { useCallback } from 'react';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import cronstrue from 'cronstrue';
import { Paragraph } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import { shouldEnableOnComplete } from '@/lib/datasource/uiStatus';

export type SourceSelectionReviewProps = {
  dataSource: core_v1_DataSource;
};

export const SourceSelectionReview = ({
  dataSource,
}: SourceSelectionReviewProps) => {
  const renderCron = useCallback((cron: string) => {
    try {
      return cronstrue.toString(cron);
    } catch {
      return `${cron} (invalid POSIX expression!)`;
    }
  }, []);
  return (
    <div>
      <div>
        <Fieldset.Field>
          <Fieldset.Label>Source</Fieldset.Label>
          <Fieldset.Value data-testid="review-source-value">
            {dataSource.spec?.source}
          </Fieldset.Value>
        </Fieldset.Field>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Source type:</Fieldset.Label>
          <Fieldset.Value data-testid="review-source-type">
            {dataSource.spec?.sourceType}
          </Fieldset.Value>
        </Fieldset.Field>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Preset:</Fieldset.Label>
          <Fieldset.Value data-testid="review-source-preset">
            {!!dataSource.spec.usePreset && (
              <>
                {dataSource.spec.usePreset}{' '}
                {dataSource.spec.usePresetVersion &&
                  `v${dataSource.spec.usePresetVersion}`}
              </>
            )}
          </Fieldset.Value>
        </Fieldset.Field>
        <Fieldset.List>
          <Paragraph className="pr-8">{dataSource.metadata?.comment}</Paragraph>
        </Fieldset.List>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Name & description:</Fieldset.Label>
          <Fieldset.Value data-testid="review-datasource-name">
            {dataSource.metadata?.name}
          </Fieldset.Value>
        </Fieldset.Field>
        <Fieldset.List>
          <Paragraph
            className="pr-8"
            data-testid="review-datasource-description"
          >
            {dataSource.metadata?.comment}
          </Paragraph>
        </Fieldset.List>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Helper notebook:</Fieldset.Label>
          <Fieldset.Value data-testid="review-helper-notebook">
            {dataSource.spec?.custom?.notebook || 'None'}
          </Fieldset.Value>
        </Fieldset.Field>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Processing schedule</Fieldset.Label>
        </Fieldset.Field>
        <Fieldset.List>
          {dataSource.spec?.schedule?.atLeastEvery && (
            <Fieldset.Field>
              <Fieldset.Label>At least every:</Fieldset.Label>
              <Fieldset.Value data-testid="review-schedule-atleast-every">
                {dataSource?.spec?.schedule?.atLeastEvery}
              </Fieldset.Value>
            </Fieldset.Field>
          )}
          {dataSource.spec?.schedule?.continuous && (
            <Fieldset.Field>
              <Fieldset.Value data-testid="review-schedule-continuous">
                Continuous
              </Fieldset.Value>
            </Fieldset.Field>
          )}
          {dataSource.spec?.schedule?.exactly && (
            <Fieldset.Field>
              <Fieldset.Value data-testid="review-cron-expression">
                {renderCron(dataSource?.spec?.schedule?.exactly)}
              </Fieldset.Value>
            </Fieldset.Field>
          )}
          <Fieldset.Field>
            <Fieldset.Label>Compute resource:</Fieldset.Label>
            <Fieldset.Value data-testid="review-compute-resource">
              {dataSource.spec?.schedule?.computeGroup}
            </Fieldset.Value>
          </Fieldset.Field>
        </Fieldset.List>
      </div>

      <Spacer size="lg" />

      <div>
        <Fieldset.Field>
          <Fieldset.Label>Datasource enabled:</Fieldset.Label>
          <Fieldset.Value data-testid="review-datasource-enabled">
            {shouldEnableOnComplete(dataSource) ||
            dataSource.spec?.schedule?.enabled
              ? 'Yes'
              : 'No'}
          </Fieldset.Value>
        </Fieldset.Field>
      </div>
    </div>
  );
};
